import React from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import OfferPartnerConsent from './OfferPartnerConsent';
import OfferPartnerConsentSummary from './OfferPartnerConsentSummary';
import { loadPartnerConsent, updateOfferPartnerConsents, uploadOfferConsentDocument, printOfferClientConsents } from '../actionCreators';
import { buildDownloadUrl, buildDMSDownloadUrl } from 'features/document/edit/documents/actionCreators';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import memoize from 'memoize-one';
import { getOfferPartnerConsents, getConsentsIsLoaded } from '../selectors';
import { CollapseCard } from '@totalsoft/rocket-ui';

class OfferPartnerConsentContainer extends React.PureComponent {
  state = {
    prevProps: {
      consents: null
    },
    consents: null,
    loading: {}
  };

  static getDerivedStateFromProps(props, state) {
    const { prevProps } = state;

    if (props.consents !== prevProps.consents) {
      return {
        prevProps: {
          ...prevProps,
          consents: props.consents
        },
        consents: (props.consents || []).map(editableDocument.create)
      };
    }

    return null;
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isLoaded !== this.props.isLoaded && !this.props.isLoaded) {
      this.loadData();
    }
  }

  loadData = async () => {
    const { actions, documentId, siteId, partnerId, isAssociatedPartner } = this.props;
    await actions.loadPartnerConsent(documentId, siteId, partnerId, isAssociatedPartner);
  };

  getAssignedConsents = memoize(consents => {
    return consents ? consents.filter(x => x.selected) : [];
  });

  handleChange = (index, prop, data) => {
    this.setState(prevState => ({
      consents: collection.update(index, editableDocument.update(prop, data, null), prevState.consents)
    }));
  };

  handleUpload = async (index, file, updateProgress) => {
    const { documentId, siteId, partnerId } = this.props;
    const { fileId, consentTypeId } = this.state.consents[index].document;

    try {
      let event = await this.props.actions.uploadOfferConsentDocument(
        documentId,
        siteId,
        { fileId, consentTypeId, file, partnerId },
        updateProgress
      );
      if (!event.ok) {
        return;
      }

      this.handleChange(index, 'consentFileId', event.eventBody.fileId);
    } catch (error) {
      // continue regardless of error
    }
  };

  handleDownload = async fileId => {
    try {
      let link = this.props.actions.buildDMSDownloadUrl(fileId);
      window.location = link;
    } catch (error) {
      // continue regardless of error
    }
  };

  handleDownloadPrints = fileArray => {
    const { documentId, siteId } = this.props;
    for (var i = 0; i < fileArray.length; i++) {
      let link = this.props.actions.buildDownloadUrl(documentId, siteId, fileArray[i].filePath, fileArray[i].fileName);
      setTimeout(
        function (path) {
          window.location = path;
        },
        1 + i * 2000,
        link
      );
    }
  };

  onSubmit = async () => {
    const { consents } = this.state;
    const { documentId, siteId, partnerId, isAssociatedPartner } = this.props;

    this.setState({
      loading: { form: true }
    });

    try {
      const partnerConsents = {
        documentId,
        siteId,
        partnerId,
        options: consents.filter(x => x.dirtyInfo.isDirty).map(x => x.document)
      };
      await this.props.actions.updateOfferPartnerConsents(partnerConsents, isAssociatedPartner);
    } catch (error) {
      // continue regardless of error
    }
    this.setState({
      loading: { form: false },
      consents: consents.map(x => {
        return { ...x, dirtyInfo: { isDirty: false } };
      })
    });
  };

  onPrint = async () => {
    const { consents } = this.state;

    this.setState({
      loading: { print: true }
    });

    const partnerConsents = {
      documentId: this.props.documentId,
      siteId: this.props.siteId,
      partnerId: this.props.offerHeader.client.clientId,
      options: consents.map(x => x.document)
    };

    try {
      const event = await this.props.actions.printOfferClientConsents(partnerConsents);
      if (event.ok) {
        if (event.eventBody.files && event.eventBody.files.length > 0) {
          this.handleDownloadPrints(event.eventBody.files);
        }
      }
    } catch (error) {
      // continue regardless of error
    }
    this.setState({ loading: { print: false } });
  };

  goToSigning = async () => {
    this.props.onPrintClick();
  };

  render() {
    const { t } = this.props;
    const { consents, loading } = this.state;
    const consentItems = consents.map(x => x.document);
    const consentInfoIsDirty = consents.some(x => x.dirtyInfo.isDirty);

    return (
      <CollapseCard
        id={'offerPartnerConsentCard'}
        icon={VerifiedUser}
        title={t('OfferEdit.Client.Consent.Title')}
        subheader={<OfferPartnerConsentSummary assignedConsents={this.getAssignedConsents(consentItems)} />}
        hideSubheaderOnExpand
        toggleOnHeaderClick
      >
        {consentItems && consentItems.length > 0 ? (
          <OfferPartnerConsent
            consents={consentItems}
            loading={loading}
            handleChange={this.handleChange}
            handleUpload={this.handleUpload}
            handleDownload={this.handleDownload}
            onSubmit={this.onSubmit}
            onPrint={this.onPrint}
            goToSigning={this.goToSigning}
            consentInfoIsDirty={consentInfoIsDirty}
          />
        ) : (
          <></>
        )}
      </CollapseCard>
    );
  }
}

OfferPartnerConsentContainer.propTypes = {
  actions: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  consents: PropTypes.array.isRequired,
  isLoaded: PropTypes.bool.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  partnerId: PropTypes.number.isRequired,
  isAssociatedPartner: PropTypes.bool,
  offerHeader: PropTypes.object.isRequired,
  onPrintClick: PropTypes.func.isRequired
};

function mapStateToProps(state, ownProps) {
  return {
    consents: getOfferPartnerConsents(state, ownProps.partnerId),
    isLoaded: getConsentsIsLoaded(state, ownProps.partnerId),
    offerHeader: state.currentOffer.header
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadPartnerConsent,
        updateOfferPartnerConsents,
        uploadOfferConsentDocument,
        printOfferClientConsents,
        buildDMSDownloadUrl,
        buildDownloadUrl
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withTranslation())(OfferPartnerConsentContainer);
